import React, { useState, useMemo } from 'react';
import {
    Form,
    Radio,
    Select,
    Input,
    Spin,
    Alert,
} from 'antd';
import { debounce } from 'lodash-es';
import * as lessonPlanApi from '../services/lessonPlanApi';
import SyllabusTopicConceptSelect, { SELECT_LEVELS } from '@common/SyllabusTopicConceptSelectB';
import { getIcon } from '../utils/iconMap';

const { Option } = Select;

const LearningObjective = ({ topicSource, options }) => {
    const form = Form.useFormInstance();
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchResults, setSearchResults] = useState([]);

    const handleAcaraSearch = useMemo(() =>
            debounce(async (query) => {
                if (!query || query.length < 3) {
                    setSearchResults([]);
                    return;
                }
                setSearchLoading(true);
                try {
                    const results = await lessonPlanApi.searchAcaraCodes(query);
                    setSearchResults(results || []);
                } catch (err) {
                    displayToastMessage(err.message || "Failed to search ACARA codes.", null, 'error', null, 10000);
                    setSearchResults([]);
                } finally {
                    setSearchLoading(false);
                }
            }, 500),
        []
    );

    const handleAcaraSelect = (selectedCode) => {
        const selectedStandard = searchResults.find(r => r.code === selectedCode);
        if (selectedStandard) {
            form.setFieldValue('acaraDescription', selectedStandard.description);
        } else {
            form.setFieldValue('acaraDescription', undefined);
        }
    };

    const handleConceptChange = (activeObject) => {
        console.log('Syllabus/Topic/Concept selection changed, active object:', activeObject);
    };

    const initialSyllabusIds = useMemo(() => {
        if (!form) return null;
        const { syllabusGroupId, syllabusId, topicId, conceptId } = form.getFieldsValue(true);
        if (syllabusGroupId || syllabusId || topicId || conceptId) {
            return { syllabusGroupId, syllabusId, topicId, conceptId };
        }
        return null;
    }, [form]);

    const handleSyllabusLoadComplete = (state) => {
        if (!state) return;
        console.log('handleSyllabusLoadComplete', state);
    };

    const { learningGoals = [], knowledgeDimensions = [], dokLevels = [] } = options || {};

    return (
        <div>
            <div className={'ch-lpm-form-section'}>
                <h3>Learning Topic</h3>
                <Form.Item
                    label="How do you want to define your topic?"
                    name="topicSource"
                    rules={[{ required: true, message: 'Please select a topic source.' }]}
                >
                    <Radio.Group
                        buttonStyle={'solid'}
                    >
                        <Radio.Button value="TEXTBOOK">From Textbook</Radio.Button>
                        <Radio.Button value="ACARA_CODE">By ACARA Code</Radio.Button>
                    </Radio.Group>
                </Form.Item>

                

                {topicSource === 'TEXTBOOK' && (
                    <>
                        <Alert
                            message="Select a syllabus, topic, and concept to ground the AI generation in your curriculum."
                            type="info"
                            showIcon
                        />
                        <div className="ch-lpm-syllabus-select-row">
                            <SyllabusTopicConceptSelect
                                form={form}
                                selectLevel={SELECT_LEVELS.CONCEPT}
                                onChange={handleConceptChange}
                                onLoadComplete={handleSyllabusLoadComplete}
                                initialIds={initialSyllabusIds}
                            />
                        </div>
                    </>
                )}

                {topicSource === 'ACARA_CODE' && (
                    <div>
                        <Alert
                            message="Search for an ACARA code or keyword to ground the AI generation in a specific standard."
                            type="info"
                            showIcon
                        />
                        <Form.Item
                            label="Search & Select Standard"
                            name="acaraCode"
                            rules={[{ required: true, message: 'Please select a standard.' }]}
                        >
                            <Select
                                placeholder="e.g., AC9M8N01 or 'Pythagoras'"
                                loading={searchLoading}
                                onChange={handleAcaraSelect}
                                showSearch
                                filterOption={false}
                                onSearch={handleAcaraSearch}
                                notFoundContent={searchLoading ? <Spin size="small" /> : null}
                                allowClear
                            >
                                {searchResults.map(r => (
                                    <Option key={r.code} value={r.code}>
                                        <strong>{r.code}</strong> {r.strand ? `${r.strand}: ` : ''}{r.description}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item name="acaraDescription" hidden><Input /></Form.Item>
                    </div>
                )}
            </div>

            <div className={'ch-lpm-form-section'}>
                <h3>Learning Goal</h3>
                <Form.Item label="What is the main purpose of this lesson?" name="learningGoal" rules={[{ required: true }]}>
                    <Radio.Group optionType="button" buttonStyle="solid" className="ch-lpm-learning-goal-group">
                        <div className="ch-lpm-radio-grid">
                            {learningGoals.map((item) => (
                                <Radio.Button key={item.value} value={item.value} className="ch-lpm-radio-card">
                                    <div className="ch-lpm-radio-card-content">
                                        {React.cloneElement(getIcon(item.icon), { className: 'ch-lpm-radio-card-icon' })}
                                        <span className="ch-lpm-radio-card-label">{item.label}</span>
                                    </div>
                                </Radio.Button>
                            ))}
                        </div>
                    </Radio.Group>
                </Form.Item>
                <Form.Item label="What is the primary type of knowledge?" name="knowledgeDimension" rules={[{ required: true }]}>
                    <Radio.Group optionType="button" buttonStyle="solid" className="ch-lpm-knowledge-group">
                        <div className="ch-lpm-knowledge-grid">
                            {knowledgeDimensions.map((item) => (
                                <Radio.Button key={item.value} value={item.value} className="ch-lpm-knowledge-card">
                                    <div className="ch-lpm-knowledge-card-content">
                                        {React.cloneElement(getIcon(item.icon), { className: 'ch-lpm-knowledge-card-icon' })}
                                        <div className="ch-lpm-knowledge-card-text">
                                            <strong>{item.label.split(':')[0]}:</strong>
                                            <span>{item.label.split(':')[1]}</span>
                                        </div>
                                    </div>
                                </Radio.Button>
                            ))}
                        </div>
                    </Radio.Group>
                </Form.Item>
                <Form.Item label="What is the main cognitive task for students?" name="dokLevel" rules={[{ required: true }]}>
                    <Radio.Group optionType="button" buttonStyle="solid" className="ch-lpm-cognitive-group">
                        <div className="ch-lpm-cognitive-list">
                            {dokLevels.map((item) => (
                                <Radio.Button key={item.value} value={item.value} className="ch-lpm-cognitive-item">
                                    <div className="ch-lpm-cognitive-item-content">
                                        {React.cloneElement(getIcon(item.icon), { className: 'ch-lpm-cognitive-item-icon' })}
                                        <span className="ch-lpm-cognitive-item-label">{item.label}</span>
                                    </div>
                                </Radio.Button>
                            ))}
                        </div>
                    </Radio.Group>
                </Form.Item>
            </div>
        </div>
    );
};

export default LearningObjective;
